import { TrialProgram, type CCCaseResource } from '#entities';
import {
  ENOPCaseResourceType,
  EResourceType,
  type ENOPOrganType,
} from '#shared';

export type SearchableCase = {
  label?: string | null;
  flightIds?: string[] | null;
  resources?: CCCaseResource[] | null;
  organ?: ENOPOrganType | undefined;
  trialProgram: TrialProgram | null;
};

// UI needs to render rows with icons
export type MatchRow = {
  iconKey?: EResourceType;
  text: string;
};

export type MatchDetails = {
  matched: boolean;
  flightIds?: string[]; // shown as plain line (no icon)
  rows?: MatchRow[]; // rows with icons (surgeon/specialist/device)
};

const norm = (s?: string | null) => (s ? s.trim().toLowerCase() : '');
const hit = (field?: string | null, q?: string) =>
  !!q && !!field && field.toLowerCase().includes(q);

// map case-resource types to UI icon types
const mapResourceTypeToIcon = (
  t?: ENOPCaseResourceType | null,
): EResourceType | undefined => {
  switch (t) {
    case ENOPCaseResourceType.Surgeon:
      return EResourceType.Surgeon;
    case ENOPCaseResourceType.ClinicalSpecialist:
      return EResourceType.Specialist;
    case ENOPCaseResourceType.OCSConsole:
      return EResourceType.Device;
    default:
      return undefined;
  }
};

export const getMatchDetails = (
  item: SearchableCase,
  query: string,
): MatchDetails => {
  const q = norm(query);
  if (!q) {
    return { matched: true };
  }

  const details: MatchDetails = { matched: false };
  const rows: MatchRow[] = [];

  // label/flightIds
  if (hit(item.label, q)) {
    details.matched = true;
  }
  const matchedFlights = (item.flightIds ?? []).filter(fid => hit(fid, q));
  if (matchedFlights.length) {
    details.matched = true;
    details.flightIds = matchedFlights;
  }

  // icon rows ONLY for matched fields
  for (const res of item.resources ?? []) {
    const rd = res.resourceData;
    const rType = rd?.resourceType;
    if (rType === ENOPCaseResourceType.Aircraft) {
      continue;
    } // skip

    const iconKey = mapResourceTypeToIcon(rType);

    if (
      rType === ENOPCaseResourceType.Surgeon ||
      rType === ENOPCaseResourceType.ClinicalSpecialist
    ) {
      const first = rd?.firstName ?? '';
      const last = rd?.lastName ?? '';
      const full = [first, last].filter(Boolean).join(' ').trim();
      if (hit(first, q) || hit(last, q) || hit(full, q)) {
        details.matched = true;
        if (full) {
          rows.push({ iconKey, text: full });
        }
      }
    } else if (rType === ENOPCaseResourceType.OCSConsole) {
      const name = rd?.resourceName ?? '';
      const serial = rd?.serialNumber ?? '';
      const pieces: string[] = [];
      if (hit(name, q)) {
        pieces.push(name);
      }
      if (hit(serial, q)) {
        pieces.push(serial);
      }
      if (pieces.length) {
        details.matched = true;
        rows.push({ iconKey, text: pieces.join(' - ') });
      }
    }
  }

  if (rows.length) {
    details.rows = rows;
  }
  return details;
};

const extractFlightIds = (c): string[] | null => {
  if (Array.isArray(c?.flightIds)) {
    return c.flightIds.filter(Boolean);
  }
  return null;
};

export const toSearchableCase = (c): SearchableCase => ({
  label: c?.label ?? null,
  flightIds: extractFlightIds(c),
  resources: (c?.resources as CCCaseResource[] | null) ?? null,
  organ: c?.organ?.name as ENOPOrganType | undefined,
  trialProgram: c?.trialProgram ?? null,
});
